/**
 * Checkout Page
 * 
 * Supports:
 * - Plan selection (Free, Starter, Pro, Enterprise)
 * - Multiple add-ons (CI Proof Pro, Signals API, Market Signals)
 * - Add-ons can be selected even with Free plan
 * - Clear line-item pricing
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ShieldCheck, Check, Sparkles, Calendar, CreditCard,
  Code2, Eye, TrendingUp, ArrowRight, Store, X, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { CILogo } from '../components/CILogo';
import { stripeService } from '../services/stripe';

// Pricing data
const PLANS = {
  free: {
    id: 'free',
    name: 'Free',
    price: { monthly: 0, annual: 0 },
    description: 'CI Score + TheDeals presence — get started in minutes',
    features: [
      'CI Score + Commerce Health',
      'CI Verified profile',
      'Publish 5 deals/mo (3 active) to TheDeals',
      'Basic analytics (clicks, saves)',
      '30 WhatsApp/SMS messages/mo included',
      'CI Proof widget (10 products)'
    ]
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    price: { monthly: 79, annual: 758 },
    description: 'Distribution + Demand — Shopify sync + demand pools',
    features: [
      'Everything in Free',
      'Shopify product + inventory sync',
      'Unlimited TheDeals publishing (30 active)',
      'Demand Pools + Offer Requests (read-only)',
      '500 messages/mo included',
      'Weekly Operator Brief'
    ]
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    price: { monthly: 299, annual: 2870 },
    description: 'Ops + Decisions + Product — full platform access',
    popular: true,
    features: [
      'Everything in Starter',
      'Slack actions + Decision traces',
      'Feedback / NPS engine + VoC report',
      'Offer Builder (create + publish)',
      'Precedent Library + Pricing Agent',
      '2,000 messages/mo included',
      'Priority onboarding'
    ]
  },
  scale: {
    id: 'scale',
    name: 'Scale',
    price: { monthly: 999, annual: 9590 },
    description: 'Funding + Signals API + Teams — enterprise-grade',
    features: [
      'Everything in Pro',
      'Funding underwriting pack generator',
      'Signals API included',
      'Team workflows + escalation',
      'Advanced guardrails + governance',
      '10,000 messages/mo included',
      'Quarterly ops review'
    ]
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    price: { monthly: null, annual: null },
    description: 'Omni-Channel — custom integrations + SSO',
    features: [
      'Everything in Scale',
      'Multi-system adapters (OMS/WMS/POS)',
      'Custom benchmarks + cohorts',
      'RBAC + audit + SSO',
      'Dedicated support'
    ]
  }
};

const ADDONS = {
  ci_proof_pro: {
    id: 'ci_proof_pro',
    name: 'CI Proof Pro Widget',
    price: 29,
    description: 'Full catalog + styling controls',
    icon: Eye,
    color: 'green',
    details: [
      'Full catalog coverage (no 10-product limit)',
      'Custom styling + placement controls',
      'A/B testing + conversion reporting'
    ]
  },
  signals_api: {
    id: 'signals_api',
    name: 'Signals API',
    price: 99,
    description: 'Brand + Product signals via API',
    icon: Code2,
    color: 'purple',
    details: [
      'Structured brand/product signals',
      'JSON API endpoints',
      'Activity tiers (hot/steady/cold)'
    ]
  },
  market_signals: {
    id: 'market_signals',
    name: 'Category + Market Signals',
    price: 199,
    description: 'Early Access — requires call',
    icon: TrendingUp,
    color: 'orange',
    requiresCall: true,
    details: [
      'Category-level intelligence',
      'Market-wide signals',
      'Planning + strategy insights'
    ]
  }
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get initial values from URL params
  const initialPlan = searchParams.get('plan') || 'starter';
  const initialAddon = searchParams.get('addon');
  const initialBilling = searchParams.get('billing') || 'monthly';
  
  // State
  const [selectedPlan, setSelectedPlan] = useState(initialPlan);
  const [selectedAddons, setSelectedAddons] = useState(
    initialAddon ? [initialAddon] : []
  );
  const [billingCycle, setBillingCycle] = useState(initialBilling);
  const [processing, setProcessing] = useState(false);
  
  // Toggle addon selection
  const toggleAddon = (addonId) => {
    if (ADDONS[addonId]?.requiresCall) {
      // Redirect to Calendly for Market Signals
      window.open('https://calendly.com/mithunkadur', '_blank');
      return;
    }
    
    setSelectedAddons(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };
  
  // Calculate total
  const calculateTotal = () => {
    const plan = PLANS[selectedPlan];
    const planPrice = plan?.price?.[billingCycle] || 0;
    
    const addonsTotal = selectedAddons.reduce((total, addonId) => {
      const addon = ADDONS[addonId];
      if (addon && !addon.requiresCall) {
        return total + addon.price;
      }
      return total;
    }, 0);
    
    return {
      planPrice,
      addonsTotal,
      total: planPrice + addonsTotal,
      isAnnual: billingCycle === 'annual'
    };
  };
  
  const totals = calculateTotal();
  
  // Handle checkout
  const handleCheckout = async () => {
    if (selectedPlan === 'enterprise') {
      // Redirect to Calendly for Enterprise
      window.open('https://calendly.com/mithunkadur', '_blank');
      return;
    }
    
    // If only free plan with no addons, just redirect to signup
    if (selectedPlan === 'free' && selectedAddons.length === 0) {
      navigate('/merchant/login?plan=free');
      return;
    }
    
    setProcessing(true);
    try {
      toast.info('Redirecting to secure checkout...');
      
      // Filter out addons that require a call
      const purchasableAddons = selectedAddons.filter(id => !ADDONS[id]?.requiresCall);
      
      // Create checkout session with plan and addons array
      const session = await stripeService.createCheckoutSession(
        selectedPlan,
        billingCycle,
        purchasableAddons.length > 0 ? purchasableAddons : null
      );
      
      // Handle different response types
      if (session.type === 'calendly') {
        window.open(session.url, '_blank');
        setProcessing(false);
        return;
      }
      
      if (session.type === 'free') {
        navigate('/merchant/login?plan=free');
        return;
      }
      
      if (session.url) {
        window.location.href = session.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setProcessing(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2">
            <CILogo size={32} showText={false} theme="light" />
            <span className="font-bold text-lg text-gray-900">Commerce Index</span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Choose Your Plan</h1>
          <p className="text-gray-600">
            {billingCycle === 'annual'
              ? 'Annual plans save 20% and are billed today. Cancel anytime.'
              : 'Start with a 14-day free trial. Cancel anytime.'}
          </p>
        </div>
        
        {/* Billing Toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                billingCycle === 'monthly' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                billingCycle === 'annual' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Annual
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>
        
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Plan Selection */}
          <div className="lg:col-span-2 space-y-6">
            {/* Plans */}
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-4">1. Select a Plan</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {Object.values(PLANS).map((plan) => (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative cursor-pointer rounded-xl border-2 p-5 transition-all ${
                      selectedPlan === plan.id
                        ? 'border-blue-600 bg-blue-50/50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-4">
                        <span className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                          Most Popular
                        </span>
                      </div>
                    )}
                    
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-gray-900">{plan.name}</h3>
                        <p className="text-sm text-gray-600">{plan.description}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedPlan === plan.id 
                          ? 'border-blue-600 bg-blue-600' 
                          : 'border-gray-300'
                      }`}>
                        {selectedPlan === plan.id && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      {plan.price[billingCycle] !== null ? (
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold text-gray-900">
                            ${plan.price[billingCycle]}
                          </span>
                          <span className="text-gray-500 text-sm">
                            /{billingCycle === 'annual' ? 'year' : 'mo'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-lg font-semibold text-gray-900">Custom pricing</span>
                      )}
                    </div>
                    
                    <ul className="space-y-2">
                      {plan.features.slice(0, 4).map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Add-ons */}
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-4">2. Add-ons (Optional)</h2>
              <p className="text-sm text-gray-600 mb-4">
                Extend Commerce Index with additional capabilities. Add-ons can be added to any plan.
              </p>
              <div className="space-y-3">
                {Object.values(ADDONS).map((addon) => {
                  const AddonIcon = addon.icon;
                  const isSelected = selectedAddons.includes(addon.id);
                  const colorClasses = {
                    green: 'border-green-300 bg-green-50/50',
                    purple: 'border-purple-300 bg-purple-50/50',
                    orange: 'border-orange-300 bg-orange-50/50'
                  };
                  const iconBgClasses = {
                    green: 'bg-green-100',
                    purple: 'bg-purple-100',
                    orange: 'bg-orange-100'
                  };
                  const iconColorClasses = {
                    green: 'text-green-600',
                    purple: 'text-purple-600',
                    orange: 'text-orange-600'
                  };
                  
                  return (
                    <div
                      key={addon.id}
                      onClick={() => toggleAddon(addon.id)}
                      className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${
                        isSelected && !addon.requiresCall
                          ? colorClasses[addon.color]
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBgClasses[addon.color]}`}>
                          <AddonIcon className={`w-5 h-5 ${iconColorClasses[addon.color]}`} />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-gray-900">{addon.name}</h3>
                              <p className="text-sm text-gray-600">{addon.description}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-lg font-bold text-gray-900">
                                +${addon.price}<span className="text-sm font-normal text-gray-500">/mo</span>
                              </span>
                              {addon.requiresCall ? (
                                <a 
                                  href="https://calendly.com/mithunkadur"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-1.5 text-sm font-semibold text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50"
                                  data-testid={`book-call-${addon.id}`}
                                >
                                  Book Call
                                </a>
                              ) : (
                                <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                                  isSelected 
                                    ? 'border-blue-600 bg-blue-600' 
                                    : 'border-gray-300'
                                }`}>
                                  {isSelected && <Check className="w-4 h-4 text-white" />}
                                </div>
                              )}
                            </div>
                          </div>
                          <ul className="mt-2 flex flex-wrap gap-2">
                            {addon.details.map((detail, i) => (
                              <li key={i} className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {detail}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Right: Order Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h2>
              
              {/* Line Items */}
              <div className="space-y-3 mb-6">
                {/* Plan */}
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium text-gray-900">{PLANS[selectedPlan].name}</span>
                    <span className="text-gray-500 text-sm ml-1">
                      ({billingCycle === 'annual' ? 'Annual' : 'Monthly'})
                    </span>
                  </div>
                  <span className="font-semibold text-gray-900">
                    {totals.planPrice > 0 ? `$${totals.planPrice}` : 'Free'}
                  </span>
                </div>
                
                {/* Add-ons */}
                {selectedAddons.map(addonId => {
                  const addon = ADDONS[addonId];
                  if (!addon || addon.requiresCall) return null;
                  return (
                    <div key={addonId} className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <Plus className="w-3 h-3 text-gray-400" />
                        <span className="text-gray-700">{addon.name}</span>
                      </div>
                      <span className="text-gray-700">+${addon.price}/mo</span>
                    </div>
                  );
                })}
                
                {selectedAddons.length === 0 && (
                  <p className="text-sm text-gray-500 italic">No add-ons selected</p>
                )}
              </div>
              
              {/* Divider */}
              <div className="border-t border-gray-200 pt-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-gray-900">
                    {billingCycle === 'annual' ? 'Total (Billed Annually)' : 'Monthly Total'}
                  </span>
                  <span className="text-2xl font-bold text-gray-900">
                    ${totals.total}
                    <span className="text-sm font-normal text-gray-500">
                      /{billingCycle === 'annual' ? 'yr' : 'mo'}
                    </span>
                  </span>
                </div>
                {totals.addonsTotal > 0 && totals.planPrice > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Base plan + ${totals.addonsTotal}/mo in add-ons
                  </p>
                )}
              </div>
              
              {/* Trial info — trials apply to monthly billing only */}
              {totals.planPrice > 0 && billingCycle === 'monthly' && (
                <div className="bg-blue-50 rounded-lg p-3 mb-6">
                  <p className="text-sm text-blue-700">
                    <Sparkles className="w-4 h-4 inline mr-1" />
                    <strong>14-day free trial</strong> — You won't be charged until trial ends
                  </p>
                </div>
              )}
              {totals.planPrice > 0 && billingCycle === 'annual' && (
                <div className="bg-amber-50 rounded-lg p-3 mb-6">
                  <p className="text-sm text-amber-700">
                    Annual billing is charged today (20% savings). Switch to
                    monthly for the 14-day free trial.
                  </p>
                </div>
              )}
              
              {/* CTA */}
              <button
                onClick={handleCheckout}
                disabled={processing}
                className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                  processing
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : selectedPlan === 'enterprise'
                    ? 'bg-gray-900 text-white hover:bg-gray-800'
                    : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                }`}
                data-testid="checkout-btn"
              >
                {processing ? (
                  <>Processing...</>
                ) : selectedPlan === 'enterprise' ? (
                  <>
                    <Calendar className="w-5 h-5" />
                    Talk to Sales
                  </>
                ) : totals.total === 0 ? (
                  <>
                    Get Started Free
                    <ArrowRight className="w-5 h-5" />
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    {billingCycle === 'annual' ? 'Subscribe Annually' : 'Start 14-Day Trial'}
                  </>
                )}
              </button>
              
              {/* Trust signals */}
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500">
                  🔒 Secure checkout powered by Stripe
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Cancel anytime • No hidden fees
                </p>
              </div>
              
              {/* Questions */}
              <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-600">
                  Have questions?{' '}
                  <a 
                    href="https://calendly.com/mithunkadur"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-medium"
                  >
                    Talk to Sales
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
